import React from 'react';
import { Link } from 'react-router-dom';
import { PRODUCT_IMAGES, FAQS } from '../data/mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

export function AboutPage() {
  return (
    <div className="fade-in">
      <section className="bg-champagne">
        <div className="mx-auto max-w-6xl px-5 py-24 lg:px-8">
          <p className="eyebrow text-espresso/60">Our story</p>
          <h1 className="mt-3 font-display text-5xl md:text-7xl">Self-Care Has No Labels.</h1>
          <p className="mt-6 max-w-2xl text-espresso/80">We believe confidence starts with how you feel about yourself. Whether it’s a skincare routine that refreshes your day, a grooming essential that completes your routine, or a timeless piece of jewelry that expresses your individuality — the little details matter.</p>
        </div>
      </section>
      <section className="mx-auto grid max-w-6xl gap-10 px-5 py-20 md:grid-cols-2 md:items-center lg:px-8">
        <img src={PRODUCT_IMAGES.catSkincare} alt="Skincare" className="aspect-[4/5] w-full object-cover" />
        <div>
          <h2 className="font-display text-4xl md:text-5xl">One brand. Every style.<br/>Everyday confidence.</h2>
          <p className="mt-6 text-espresso/80">Our collections bring together beauty, skincare, self-care and accessories in one carefully curated destination — designed for different styles, different personalities, and different expressions of confidence.</p>
          <Link to="/shop" className="mt-8 btn btn-dark inline-flex">Shop the edit</Link>
        </div>
      </section>
      <section className="mx-auto max-w-3xl px-5 py-16 lg:px-8">
        <h2 className="text-center font-display text-4xl">Good to know</h2>
        <Accordion type="single" collapsible className="mt-10">
          {FAQS.map((f, i) => (
            <AccordionItem key={i} value={`f${i}`}><AccordionTrigger>{f.q}</AccordionTrigger><AccordionContent>{f.a}</AccordionContent></AccordionItem>
          ))}
        </Accordion>
      </section>
    </div>
  );
}

export function ContactPage() {
  const [sent, setSent] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [form, setForm] = React.useState({ name: '', email: '', order_number: '', message: '' });
  const upd = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const axios = (await import('axios')).default;
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, form);
      setSent(true);
    } catch (err) {
      alert('Could not send message. Please try again.');
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fade-in mx-auto max-w-3xl px-5 py-20 lg:px-8">
      <p className="eyebrow text-espresso/60">Get in touch</p>
      <h1 className="mt-2 font-display text-5xl">We’re here to help.</h1>
      <p className="mt-4 text-espresso/70">Questions about an order, our ingredients or styling? Send a note — we’ll reply within one business day.</p>
      {sent ? (
        <div className="mt-10 border border-gold bg-ivory p-6">
          <p className="font-display text-2xl">Thank you.</p>
          <p className="mt-2 text-espresso/70">Your message is on its way. We’ll be in touch shortly.</p>
        </div>
      ) : (
        <form onSubmit={submit} className="mt-10 space-y-4">
          <input required value={form.name} onChange={upd('name')} placeholder="Your name" className="w-full border border-stone-warm bg-white px-4 py-3 outline-none focus:border-gold" />
          <input required type="email" value={form.email} onChange={upd('email')} placeholder="Email" className="w-full border border-stone-warm bg-white px-4 py-3 outline-none focus:border-gold" />
          <input value={form.order_number} onChange={upd('order_number')} placeholder="Order number (optional)" className="w-full border border-stone-warm bg-white px-4 py-3 outline-none focus:border-gold" />
          <textarea required rows="5" value={form.message} onChange={upd('message')} placeholder="Message" className="w-full border border-stone-warm bg-white px-4 py-3 outline-none focus:border-gold" />
          <button disabled={busy} className="btn btn-dark disabled:opacity-60">{busy ? 'Sending…' : 'Send message'}</button>
        </form>
      )}
    </div>
  );
}

export function WishlistPage() {
  const { wishlist } = require('../context/StoreContext').useStore();
  const { useProducts } = require('../hooks/useProducts');
  const ProductCard = require('../components/site/ProductCard').default;
  const { products } = useProducts();
  const items = products.filter((p) => wishlist.includes(p.id));
  return (
    <div className="fade-in mx-auto max-w-7xl px-5 py-16 lg:px-8">
      <h1 className="font-display text-5xl">Wishlist</h1>
      {items.length === 0 ? (
        <p className="mt-6 text-espresso/60">Nothing saved yet. Start adding pieces you love.</p>
      ) : (
        <div className="mt-10 grid gap-8 sm:grid-cols-2 lg:grid-cols-4">
          {items.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      )}
    </div>
  );
}

export function AccountPage() {
  return (
    <div className="fade-in mx-auto max-w-md px-5 py-20 lg:px-8">
      <h1 className="text-center font-display text-5xl">Account</h1>
      <p className="mt-3 text-center text-espresso/60">Sign in to view orders and your wishlist.</p>
      <form onSubmit={(e) => e.preventDefault()} className="mt-10 space-y-3">
        <input type="email" placeholder="Email" className="w-full border border-stone-warm bg-white px-4 py-3 outline-none focus:border-gold" />
        <input type="password" placeholder="Password" className="w-full border border-stone-warm bg-white px-4 py-3 outline-none focus:border-gold" />
        <button className="btn btn-dark w-full">Sign in</button>
      </form>
      <p className="mt-6 text-center text-sm text-espresso/60">New here? <a href="#" className="text-gold">Create an account</a></p>
    </div>
  );
}
